import { Notification } from '@thanx/uikit/notification'
import { buildTranslate } from 'locales'
import React, { useMemo } from 'react'
import ConfigRowModal from './ConfigRowModal'
import EscalationContactsBody from './EscalationContactsBody'
import { useEscalationContactsController } from './useEscalationContactsController'

const t = buildTranslate('recovery_ai.settings.escalation_contacts')

type Props = {
  /** Namespaces the toggle's label id and both pickers' description ids (C1). */
  instanceId: string
  onClose: () => void
  onSaved: () => void
  /** Host width override, forwarded to the shared shell. */
  width?: string
}

/**
 * The escalation-contacts config-modal row. Recipients persist via
 * PUT recovery/escalation_contacts, so like active_locations this row owns a
 * controller rather than staging through the draft context.
 *
 *   context: `notifyMode: 'staged'` folds the location-managers toggle into the
 *   footer's Save, so Cancel really does revert it (L13) — the eager write the
 *   pane used to fire could not be undone from inside a rollback modal. Invite
 *   is hidden (L28): its <Link> would navigate the page out from under the
 *   dialog and discard the staged selection.
 */
const EscalationContactsConfigRow = ({
  instanceId,
  onClose,
  onSaved,
  width,
}: Props) => {
  const contacts = useEscalationContactsController({ notifyMode: 'staged' })

  // isDirty no longer gates Save in the shell (ConfigRowModal), so this override
  // is inert for that purpose — kept only because `isDirty` remains a required
  // ConfigRowSaveAdapter member. The real protection against arming a write over
  // an unresolvable contacts resource lives in commit() itself: it refuses
  // (returns false, no PUT) when the recipient pickers were touched with no
  // baseline to diff against (useEscalationContactsController's
  // `recipientsTouched && !contacts` guard) — a notify-only edit is unaffected
  // since it never depends on the contacts baseline.
  const adapter = useMemo(
    () => ({
      ...contacts,
      isDirty: contacts.isDirty && !contacts.contactsUnavailable,
    }),
    [contacts]
  )

  return (
    <ConfigRowModal
      rowKey="escalation_contacts"
      width={width}
      adapter={adapter}
      // A failed merchant-users GET leaves no recipients to choose from, so it
      // is a hard load error. A 403/404 on the CONTACTS GET is not: the pickers
      // still render for review behind the warning below.
      load={{
        isLoading: contacts.isLoading,
        isLoadError: contacts.isUsersError,
      }}
      // A notify-only failure means the recipients DID land; the shell's generic
      // "couldn't save" would contradict that.
      saveErrorMessage={
        contacts.isNotifyOnlySaveError ? t('notify_save_error') : undefined
      }
      onClose={onClose}
      onSaved={onSaved}
    >
      {/* #3883: with the roles lookup unresolved, every RBAC admin demotes into
          ALL OTHER USERS with no badge. Non-blocking — the pickers still work. */}
      {contacts.adminGroupingUnavailable && (
        <div className="mb-m">
          <Notification kind="info" size="full">
            {t('admin_grouping_unavailable')}
          </Notification>
        </div>
      )}
      {contacts.contactsUnavailable && (
        <div className="mb-m">
          <Notification kind="warning" size="full">
            {contacts.recipientsReadOnly
              ? t('unloadable_notice')
              : t('unavailable_notice')}
          </Notification>
        </div>
      )}
      <EscalationContactsBody
        instanceId={instanceId}
        users={contacts.users}
        primaryIds={contacts.primaryIds}
        onPrimaryChange={contacts.onPrimaryChange}
        additionalIds={contacts.additionalIds}
        onAdditionalChange={contacts.onAdditionalChange}
        notifyLocationManagers={contacts.notifyLocationManagers}
        onNotifyChange={contacts.onNotifyChange}
        canManage={contacts.canManage}
        isAdmin={contacts.isUserAdmin}
        isNotifySaving={contacts.isNotifySaving}
        isBusy={contacts.isBusy}
        recipientsReadOnly={contacts.recipientsReadOnly}
        inviteAffordance="hidden"
      />
    </ConfigRowModal>
  )
}

export default EscalationContactsConfigRow
